import { useRef, useState, useEffect, useCallback } from 'react'
import { useDictation, prefersLocalAudioCapture, type DictationDoneMeta } from '../hooks/useDictation'
import { usePttManager } from '../presentation/hooks/usePttManager'
import { usePttVoiceCapture } from '../presentation/hooks/usePttVoiceCapture'
import { useAiWalkieTalkie } from '../presentation/hooks/useAiWalkieTalkie'
import { pwaApi, AnalyzeNoteResponse, ConversationItem } from '../api'
import { MlotButton } from '../components/DuzyMic'
import { SearchBar } from '../components/SearchBar'
import { EmployeeList } from '../components/EmployeeList'
import { SyncStatusBar } from '../components/SyncStatusBar'
// import { WebHidPairButton } from '../components/WebHidPairButton'
import { SttDiagnosticsOverlay } from '../presentation/components/SttDiagnosticsOverlay'
import type { ConversationState, ConversationActions } from '../hooks/useConversation'
import { useSearchV2 } from '../hooks/useSearchV2'
import { useOnline } from '../hooks/useNetwork'
import { setDictationActive } from '../pwaUpdate'
import { useNetworkSyncContext } from '../presentation/context/networkSync'
import { readCurrentAuthorId } from '../infrastructure/auth/currentUser'
import { db } from '../infrastructure/offline/AppDatabase'
import { SyncQueueRepository } from '../infrastructure/repositories/SyncQueueRepository'
import { isIncident } from '../utils/incidentParser'
import { getUnsyncedRawAudio } from '../engines/offline_storage'
import { AppDebugPanel } from '../presentation/components/AppDebugPanel'
import { checkpoint } from '../infrastructure/debug/AppDebugLog'

// Offline-First (wzorzec Strangler): repozytorium lokalnej kolejki synchronizacji
// (Dexie). Współdzieli singleton bazy `db`, więc instancję trzymamy na poziomie
// modułu — jedna na całą aplikację, dokładnie jak w `IncidentSummaryScreen`.
const noteSyncQueue = new SyncQueueRepository(db)

// Krótszy z tekstów notatki nie ma sensu wysyłać — spójne z progiem starej
// ścieżki (`onDoneRef` niżej: `text.length < 3` → SKIP).
const MIN_NOTE_LEN = 3

// Malejący licznik ID dla optymistycznych kafelków notatek (reaktywność UI po
// „cichym” zapisie). Zawsze UJEMNY, więc nie zderzy się z realnym, dodatnim
// `conversation_id` z backendu; unikatowy nawet dla kilku notatek w tej samej
// milisekundzie (samo `Date.now()` by nie wystarczyło jako klucz Reacta).
let optimisticNoteId = 0

// „Uczciwy UX” — komunikaty o realnym losie notatki po zakończeniu dyktowania.
// const NOTE_MSG_ONLINE = 'Notatka zapisana — trwa przetwarzanie.'
// const NOTE_MSG_OFFLINE = 'Zapisano offline. Notatka oczekuje na synchronizację w tle.'
// Dyktowanie offline bez lokalnej transkrypcji — `useDictation` woła `onDone('')`.
// Dwa przypadki, dwa komunikaty (poprzednio: cichy `saveSilentNote('')` no-op,
// zero informacji, notatka znikała):
//
//  • `voice-queued`  — Android, silnik `localrec`: surowe nagranie trafiło do
//    kolejki Dexie i backend przepisze je na tekst po powrocie sieci. Notatka
//    POWSTANIE, tylko z opóźnieniem.
//  • `voice-offline` — desktop/iOS, silnik `webspeech` padł offline i NIE ma
//    żadnego nagrania do odzyskania. Trzeba wpisać notatkę ręcznie.
// const NOTE_MSG_VOICE_QUEUED =
//   'Nagranie zapisane offline. Notatka pojawi się po odzyskaniu połączenia.'
// const NOTE_MSG_VOICE_OFFLINE =
//   'Brak internetu — dyktowanie głosowe niedostępne offline. Wpisz notatkę z klawiatury lub spróbuj ponownie po połączeniu.'
// Treść optymistycznego kafelka notatki głosowej nagranej OFFLINE — widoczny OD
// RAZU (parytet z online), zanim backend przepisze nagranie na tekst po sieci.
const NOTE_MSG_VOICE_PLACEHOLDER =
  '🎤 Nagranie zapisane — transkrypcja pojawi się po połączeniu.'

// Woki-Toki (PTT): treść bannera, gdy zapytanie już poleciało, a odpowiedź
// (WebSocket, `AI_RESPONSE`) jeszcze nie wróciła — patrz `useAiWalkieTalkie`.
// const AI_MSG_PENDING = 'Asystent AI: przetwarzam pytanie…'

function _t(freq: number, vol: number, durS: number) {
  try {
    const ctx = new (window.AudioContext || (window as any).webkitAudioContext)()
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.connect(gain); gain.connect(ctx.destination)
    osc.frequency.value = freq; osc.type = 'sine'
    gain.gain.setValueAtTime(vol, ctx.currentTime)
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + durS)
    osc.start(); osc.stop(ctx.currentTime + durS)
    osc.onended = () => ctx.close()
  } catch (_) { }
}
const sigTimeout = () => { _t(880, 0.15, 0.08); setTimeout(() => { _t(660, 0.25, 0.08); setTimeout(() => _t(440, 0.18, 0.1), 100) }, 150) } // F2

interface Screen1Positions {
  titleTop: number
  employeeListTop: number
  employeeListTopWorking: number
  searchBarTop: number
  searchBarBottomExpand: number  // Miejsce na rozwinięcie SearchBar w GÓRĘ
  mlotButtonTop: number
  tokenBarBottom: number
}

interface Props {
  positions: Screen1Positions
  micScale: number
  conv: ConversationState & ConversationActions
  onNavigateTo2: (fullText: string, name: string, analyzePromise?: Promise<AnalyzeNoteResponse>, isHardwarePtt?: boolean) => Promise<void>
  onResetSession: () => void
  onUnauthorized: (reason: string) => void
  isActive: boolean
  resetSignal: number
  dbg: (msg: string) => void
}

export function Screen1({ positions, micScale, conv, onNavigateTo2, onResetSession, onUnauthorized, isActive, resetSignal, dbg }: Props) {
  // Screen1 mount checkpoint (prevent duplicate)
  const mountedRef = useRef(false)
  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true
      checkpoint('SCREEN1', 'mounted', {
        isActive,
        positions: Object.keys(positions).length
      })
    }
  }, [isActive, positions])

  const [micStatus, setMicStatus] = useState<'default' | 'pushed' | 'working'>('default')

  // Bezpiecznik auto-aktualizacji PWA: nie przeładuj strony w trakcie dyktowania/obróbki.
  // Cleanup przy odmontowaniu jest konieczny — bez niego zejście z ekranu w trakcie
  // dyktowania zostawiało globalną flagę "true" na zawsze i PWA nigdy więcej się nie aktualizowało.
  useEffect(() => {
    setDictationActive(micStatus === 'working')
    return () => setDictationActive(false)
  }, [micStatus])
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [searchAnimated, setSearchAnimated] = useState(false)
  const [searchCompleted, setSearchCompleted] = useState(false)
  const search = useSearchV2()
  const isOnline = useOnline()
  // Woki-Toki: tor asystenta AI (Clean Arch). Od decyzji P0 (Input-Only PTT)
  // fizyczny przycisk słuchawki NIE wysyła już zapytań do asystenta — hook
  // zostaje wpięty wyłącznie po `clearAiAnswer`/baner, na wypadek gdyby
  // `dispatchAiQuery` wrócił do użycia w przyszłości.
  const { status: aiStatus, lastAnswer: _aiAnswer, clearAnswer: clearAiAnswer } = useAiWalkieTalkie()
  // Offline-First: ręczne wyzwolenie opróżnienia kolejki synchronizacji zaraz po
  // zakolejkowaniu notatki (analogicznie do `IncidentSummaryScreen`). Bierzemy je
  // z JEDNEGO, współdzielonego kontekstu (`NetworkSyncProvider` w App.tsx) —
  // ekran nie powołuje własnej instancji silnika synchronizacji. Stan sieci do
  // komunikatu nadal ze starego `useOnline()` powyżej.
  const { triggerSync, isSyncing } = useNetworkSyncContext()
  // 'idle' — brak komunikatu; 'processing' — zakolejkowano online; 'queued' —
  // zakolejkowano offline (czeka na sync). Steruje „uczciwym” banerem na dole E1.
  const [noteSubmitState, setNoteSubmitState] = useState<'idle' | 'processing' | 'queued' | 'voice-queued' | 'voice-offline'>('idle')
  const [preparingPhrase, setPreparingPhrase] = useState<string | null>(null)
  const [preparingPhraseColor, setPreparingPhraseColor] = useState<'green' | 'red'>('green')
  const [listModalOpen, setListModalOpen] = useState(false)
  // Ile operacji czeka w lokalnej kolejce synchronizacji (Dexie) + czy sync trwa
  // teraz (`isSyncing` z kontekstu) — widoczny dowód, że coś faktycznie się
  // dzieje. Źródłem prawdy jest NOWA kolejka `SyncQueueRepository`, a nie stary
  // `offline_pipeline` (który dla notatek dublował ten sam tor wysyłki).
  const [pendingSyncCount, setPendingSyncCount] = useState(0)
  const refreshPendingCount = useCallback(() => {
    noteSyncQueue.getPending().then(q => setPendingSyncCount(q.length)).catch(() => { })
  }, [])
  // Reconcyliacja optymistycznych kafelków notatek głosowych (offline) — patrz
  // `reconcileVoiceRef` niżej. Przez ref, bo domyka `conv` (nowa tożsamość co
  // render), a interwał ma być stały.
  const reconcileVoiceRef = useRef<() => void>(() => { })
  useEffect(() => {
    const tick = () => { refreshPendingCount(); reconcileVoiceRef.current() }
    tick()
    const interval = setInterval(tick, 3000)
    return () => clearInterval(interval)
  }, [refreshPendingCount])
  // Poll co 3 s nie łapie zmian w locie — odśwież licznik od razu, gdy tylko
  // synchronizacja się skończy (kolejka właśnie się skurczyła).
  useEffect(() => {
    if (!isSyncing) refreshPendingCount()
  }, [isSyncing, refreshPendingCount])
  // Ref na potrzeby `waitForSyncToFinish` — czyta najświeższy stan bez domknięć.
  const isSyncingRef = useRef(isSyncing)
  useEffect(() => { isSyncingRef.current = isSyncing }, [isSyncing])

  const magicTimersRef = useRef<ReturnType<typeof setTimeout>[]>([])
  const magicActiveRef = useRef(false)
  const magicStartTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Lista ładowana przez App.tsx po zalogowaniu

  const voiceSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const speechRef = useRef<any>(null)
  const dictTimer60 = useRef<ReturnType<typeof setTimeout> | null>(null)
  const detectedNameRef = useRef<string | null>(null)
  const selectedEmployeeRef = useRef<string>('')   // sync shadow dla conv.selectedEmployee
  const analyzePromiseRef = useRef<Promise<AnalyzeNoteResponse> | null>(null)
  const heuristicNameRef = useRef<string | null>(null)  // imię wykryte heurystyką w onSegment
  const searchCooldownRef = useRef(false)  // blokada 1s po wynikach wyszukiwania
  const llmRunningRef = useRef<boolean>(false)
  // true = bieżące nagranie uruchomił fizyczny przycisk słuchawki (PTT) → wynik
  // idzie do asystenta AI (Woki-Toki), a NIE do formularza notatki. Dotyk ekranu
  // (DuzyMic) zeruje tę flagę, więc stara ścieżka notatek działa bez zmian.
  const pttAiModeRef = useRef(false)
  // Auto-Submit hands-free: migawka `pttAiModeRef.current` z chwili, gdy dyktowanie
  // się zakończyło (patrz `onDoneRef` niżej) — musi PRZEŻYĆ do `doNavigate`
  // (i ewentualne czekanie na LLM w scenariuszu C), a `pttAiModeRef` jest zerowany
  // dużo wcześniej w tym samym callbacku.
  const hardwarePttNavRef = useRef(false)
  const onDoneRef = useRef<(full: string, meta?: DictationDoneMeta) => void>(() => { })
  const silenceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pendingDoneRef = useRef<string | null>(null)
  const pendingNavigateRef = useRef<string | null>(null)  // tekst czekający na zakończenie LLM
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const idleResetRef = useRef<() => void>(() => { })
  const searchResultsActiveRef = useRef(false)
  const searchAnimatingRef = useRef(false)  // true = trwa pipeline animacji wyszukiwania (kroki 1-9)

  const MAGIC_PHRASES = [
    'Analizuję kontekst…',
    'Dobieram model rozmowy…',
    'Kalibruję strategię…',
    'Generuję kroki…',
    'Finalizuję…',
  ]
  const MAGIC_BASE_DELAYS = [800, 1000, 900, 1100, 800]

  // UWAGA: Regex imion USUNIĘTY - zawsze używamy wyszukiwania kontekstowego RAG
  // extractNameFromQuery zostało usunięte aby uniknąć chaosu w pipeline

  // Heurystyka: szuka polskiego imienia gdziekolwiek w tekście (nie tylko na początku)
  // Imię = słowo z wielkiej litery z polskimi znakami, min 3 znaki, nie Pan/Pani/To/To/Jest/Nie
  const HEUR_SKIP = new Set(['Pan', 'Pani', 'To', 'Ten', 'Ta', 'Nie', 'Tak', 'Jest', 'Był', 'Była', 'Dzisiaj', 'Dziś', 'Wczoraj'])
  const extractNameHeuristic = (text: string): string | null => {
    const words = text.split(/\s+/)
    for (let i = 1; i < words.length; i++) {
      const w = words[i].replace(/[.,!?;:"""''()\-]/g, '')
      if (w.length < 3) continue
      if (!/^[A-ZŁŚŹŻĆŃÓĄĘ][a-złśźżćńóąę]{2,}$/.test(w)) continue
      if (HEUR_SKIP.has(w)) continue
      return w
    }
    // Fallback: sprawdź też pierwsze słowo
    if (words.length > 0) {
      const w = words[0].replace(/[.,!?;:"""''()\-]/g, '')
      if (w.length >= 3 && /^[A-ZŁŚŹŻĆŃÓĄĘ][a-złśźżćńóąę]{2,}$/.test(w) && !HEUR_SKIP.has(w)) {
        return w
      }
    }
    return null
  }

  const stopMagicPhrases = useCallback(() => {
    magicActiveRef.current = false
    magicTimersRef.current.forEach(t => clearTimeout(t))
    magicTimersRef.current = []
    if (magicStartTimerRef.current) {
      clearTimeout(magicStartTimerRef.current)
      magicStartTimerRef.current = null
    }
    setPreparingPhrase(null)
    setPreparingPhraseColor('green')
  }, [])

  const startMagicPhrases = useCallback(() => {
    stopMagicPhrases()
    magicActiveRef.current = true
    let cumDelay = 0
    MAGIC_PHRASES.forEach((phrase, i) => {
      const jitter = Math.floor(Math.random() * 160) - 80
      cumDelay += (MAGIC_BASE_DELAYS[i] ?? 1000) + jitter
      const t = setTimeout(() => {
        if (!magicActiveRef.current) return
        setPreparingPhrase(phrase)
      }, cumDelay)
      magicTimersRef.current.push(t)
    })
  }, [stopMagicPhrases])

  // ── Offline-First: standardowa notatka (dotyk ekranu / dyktowanie ręczne) ──
  // Zamiast strzelać treścią prosto z `api.ts`, KAŻDĄ podyktowaną notatkę
  // kładziemy najpierw w lokalnej kolejce Dexie i od razu wypychamy kolejkę
  // (`triggerSync`) — 1:1 jak `IncidentSummaryScreen`. Backend przyjmuje ją na
  // `POST /api/sync` jako `note/create` (SyncController → CreateNoteUseCase).
  //
  // Do payloadu ZAWSZE dokładamy `capturedAt` (ISO z chwili zakończenia
  // dyktowania — to ona, a nie zegar backendu, wyznacza `createdAt`) oraz
  // `authorId` z JWT (`null`, gdy tożsamości nie da się ustalić — backend to
  // dopuszcza). Fire-and-forget: UI nie czeka na wynik; offline
  // `OfflineSyncService` zostawia wpis w kolejce i dokończy po 'online'.
  const enqueueNoteOffline = useCallback(async (content: string, capturedAt: string): Promise<void> => {
    const authorId = await readCurrentAuthorId()
    try {
      await noteSyncQueue.enqueue('note', 'create', { content, capturedAt, authorId })
    } catch (err) {
      console.error('[Screen1] enqueue notatki do kolejki sync nieudany:', err)
    }
    triggerSync().catch(console.error)
  }, [triggerSync])

  // ── Offline-First: KANONICZNY „cichy” zapis notatki ────────────────────────
  // JEDNO miejsce, w którym treść notatki ląduje w lokalnej kolejce Dexie i od
  // razu wyzwalana jest synchronizacja. Używane IDENTYCZNIE przez trzy ścieżki:
  //   • Enter w polu „Mów lub napisz…”                (`handleTypedNoteSubmit`),
  //   • sprzętowy / symulowany PTT, treść nie-incydentowa   (`onDoneRef` niżej),
  //   • zakończone dyktowanie dotykiem / ręcznie            (`onDoneRef` niżej).
  // Dzięki temu wszystkie zapisują tak samo (payload `note/create`, „uczciwy”
  // baner online/offline, odświeżenie licznika kolejki) i nie da się ich
  // rozjechać przy kolejnych zmianach. Przyjmuje GOŁY string (nie `Event`, nie
  // stan lokalny) — bo wołają go i handler pola tekstowego, i callback PTT z
  // transkryptem. Zwraca `true`, gdy notatka trafiła do kolejki; `false`, gdy
  // była krótsza niż `MIN_NOTE_LEN` i została pominięta bez żadnego efektu.
  const saveSilentNote = useCallback((text: string): boolean => {
    const content = text.trim()
    if (content.length < MIN_NOTE_LEN) return false
    // Jeden znacznik czasu dla kolejki Dexie i dla optymistycznego kafelka —
    // żeby lista i backend miały identyczne `createdAt`.
    const capturedAt = new Date().toISOString()
    void enqueueNoteOffline(content, capturedAt)

    // ── Reaktywność UI: kafelek notatki widoczny NATYCHMIAST ──────────────────
    // Nie czekamy na round-trip do backendu ani na pełne przeładowanie listy —
    // wstrzykujemy notatkę na GÓRĘ `conv.conversationItems` (tego samego stanu,
    // z którego `EmployeeList` renderuje kafelki). `_optimistic: true` chroni
    // wpis przed nadpisaniem przez polling indeksowania w `useConversation`.
    const optimisticNote: ConversationItem = {
      conversation_id: --optimisticNoteId,  // zawsze ujemne → brak kolizji z backendem
      employee_id: 0,
      first_name: 'Notatka',
      last_name: '',
      position: null,
      short_summary: content,
      long_summary: content,
      arrangements_transcription: '',
      arrangements_json: [],
      conversation_status: 'ACCEPTABLE',
      created_at: capturedAt,
      is_indexing: false,
      _optimistic: true,
    }
    conv.setConversationItems(prev => [optimisticNote, ...(prev ?? [])])

    setNoteSubmitState(isOnline ? 'processing' : 'queued')
    refreshPendingCount()
    return true
  }, [enqueueNoteOffline, isOnline, refreshPendingCount, conv])

  // ── Offline-First: notatka GŁOSOWA bez lokalnej transkrypcji ────────────────
  // Android offline: `useDictation` tylko nagrywa audio (silnik `localrec` / tor
  // Deepgram z niedostępnym WS) i woła `onDone('', { offlineAudioQueued, audioHash })`.
  // Żeby offline zachowywało się JAK ONLINE (kafelek widać od razu), wstrzykujemy
  // placeholder notatki na górę listy. `_voiceHash` wiąże go z nagraniem w Dexie —
  // `reconcileVoiceRef` zdejmie placeholder i wciągnie prawdziwą, przepisaną
  // notatkę z backendu, gdy tylko `offline_pipeline.syncAudio` wyśle nagranie.
  const addVoicePlaceholder = useCallback((audioHash: string) => {
    const capturedAt = new Date().toISOString()
    const placeholder: ConversationItem = {
      conversation_id: --optimisticNoteId,
      employee_id: 0,
      first_name: 'Notatka głosowa',
      last_name: '',
      position: null,
      short_summary: NOTE_MSG_VOICE_PLACEHOLDER,
      long_summary: NOTE_MSG_VOICE_PLACEHOLDER,
      arrangements_transcription: '',
      arrangements_json: [],
      conversation_status: 'ACCEPTABLE',
      created_at: capturedAt,
      is_indexing: false,
      _optimistic: true,
      _voicePlaceholder: true,
      _voiceHash: audioHash,
    }
    conv.setConversationItems(prev => {
      // Idempotencja — gdyby `onDone` przyszło dwa razy dla tego samego nagrania.
      if ((prev ?? []).some(it => it._voiceHash === audioHash)) return prev ?? []
      return [placeholder, ...(prev ?? [])]
    })
    refreshPendingCount()
  }, [conv, refreshPendingCount])

  // Placeholder notatki głosowej żyje, dopóki jego nagranie czeka w Dexie
  // (`raw_audio`). Gdy `offline_pipeline.syncAudio` wyśle je na backend i skasuje
  // lokalną kopię, hash znika z kolejki → backend ma już utworzoną, przepisaną
  // notatkę → zdejmujemy placeholder i dociągamy prawdziwe rozmowy (zachowując
  // pozostałe optymistyczne kafelki, wzorzec z indexing-poll w `useConversation`).
  reconcileVoiceRef.current = () => {
    const items = conv.conversationItems ?? []
    const placeholders = items.filter(it => it._voicePlaceholder && it._voiceHash)
    if (placeholders.length === 0) return
    getUnsyncedRawAudio()
      .then(pending => {
        const pendingHashes = new Set(pending.map(p => p.hash))
        const resolvedIds = new Set(
          placeholders.filter(it => !pendingHashes.has(it._voiceHash as string)).map(it => it.conversation_id)
        )
        if (resolvedIds.size === 0) return
        pwaApi.getConversations()
          .then(r => {
            conv.setConversationItems(prev => {
              const kept = (prev ?? []).filter(it => !resolvedIds.has(it.conversation_id))
              const optimistic = kept.filter(it => it._optimistic)
              const optIds = new Set(optimistic.map(i => i.conversation_id))
              const backend = r.items.filter(i => !optIds.has(i.conversation_id))
              return [...optimistic, ...backend].sort(
                (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
              )
            })
          })
          .catch(() => {
            // Backend nieosiągalny — zdejmij same placeholdery; prawdziwa notatka
            // dojdzie przy kolejnym pollingu rozmów w `useConversation`.
            conv.setConversationItems(prev => (prev ?? []).filter(it => !resolvedIds.has(it.conversation_id)))
          })
      })
      .catch(() => { })
  }

  // ── Offline-First: notatka WPISANA z klawiatury ─────────────────────────────
  // Pole „Mów lub napisz…” w SearchBarze jest domyślnie polem NOTATKI (tryb
  // wyszukiwania włącza dopiero lupka). Enter w tym trybie wpada tutaj: ten sam
  // kanoniczny zapis co dyktowanie (`saveSilentNote` → kolejka Dexie → `POST
  // /api/sync` note/create), tyle że bez pipeline'u odprawy E2/E3 i bez detekcji
  // imienia — to zwykła, szybka notatka tekstowa. `capturedAt` = chwila wysłania.
  //
  // WYJĄTEK — detekcja słów kluczowych: zanim tekst zostanie zapisany jako
  // notatka, przepuszczamy go przez `isIncident()` (dokładnie ten sam detektor,
  // którego używa sprzętowy PTT w `onDoneRef` i offline'owy klasyfikator).
  // Jeśli to zgłoszenie („awaria", „wyciek", „serwis", „brak na magazynie"…),
  // BLOKUJEMY cichy zapis notatki i wchodzimy w tor incydentu przez
  // `onNavigateTo2` → `goE1toE2` → `parseIncident` → formularz E2. Tekst mamy od
  // razu, więc działa IDENTYCZNIE online i offline (patrz też pkt „Audio w
  // Offline” — tam brak lokalnej transkrypcji, więc nagranie zostaje notatką).
  const handleTypedNoteSubmit = useCallback((raw: string) => {
    checkpoint('SCREEN1', 'handleTypedNoteSubmit', { raw: raw.slice(0, 100) })
    const text = raw.trim()
    if (text.length >= MIN_NOTE_LEN && isIncident(text)) {
      dbg('TYPED_INCIDENT — trasuję jak zgłoszenie: ' + text.slice(0, 40))
      setSearchQuery('')
      conv.setTranscriptionText('')
      void onNavigateTo2(text, '')
      return
    }
    if (!saveSilentNote(raw)) return
    // Po zapisie czyścimy pole i transkrypt — SearchBar wraca do pustego
    // „Mów lub napisz…”.
    setSearchQuery('')
    conv.setTranscriptionText('')
  }, [saveSilentNote, conv, onNavigateTo2, dbg])

  // „Uczciwy UX”: baner znika sam po chwili — offline dajemy dłużej na przeczytanie.
  useEffect(() => {
    if (noteSubmitState === 'idle') return
    // Offline dostaje więcej czasu na przeczytanie; komunikat „głosowe offline”
    // jest najdłuższy → jeszcze dłużej.
    const dismissMs = noteSubmitState === 'voice-offline' ? 5000
      : (noteSubmitState === 'queued' || noteSubmitState === 'voice-queued') ? 3500
        : 2200
    const t = setTimeout(() => setNoteSubmitState('idle'), dismissMs)
    return () => clearTimeout(t)
  }, [noteSubmitState])

  // Woki-Toki: baner z odpowiedzią asystenta AI znika sam — user usłyszał ją
  // (TTS) i przeczytał na ekranie, nie musi ręcznie kasować.
  useEffect(() => {
    if (aiStatus !== 'answered') return
    const t = setTimeout(() => clearAiAnswer(), 6000)
    return () => clearTimeout(t)
  }, [aiStatus, clearAiAnswer])

  const startSpeech = (onResult: (text: string) => void, onEnd: () => void) => {
    // Android: `webkitSpeechRecognition.start()` otwiera natywne, systemowe okno
    // „Dyktuj" (Google Voice Typing) — przejmuje mikrofon i fokus WebView, przez
    // co pola tekstowe głuchną. Wyszukiwanie głosowe TAP-em w mikrofon jest tam
    // wyłączone (łagodny no-op, jak przy braku Web Speech); szukanie idzie przez
    // lupkę + wpisany tekst. Dyktowanie notatki (hold) działa dalej — tam
    // `useDictation` nagrywa surowym `MediaRecorder`em (patrz `prefersLocalAudioCapture`).
    if (prefersLocalAudioCapture()) { dbg('SR_SKIP_ANDROID'); onEnd(); return null }
    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition
    if (!SR) { onEnd(); return null }
    const sr = new SR()
    let gotResult = false
    sr.lang = 'pl-PL'; sr.continuous = false; sr.interimResults = false
    sr.onresult = (e: any) => { gotResult = true; onResult(e.results[0]?.[0]?.transcript ?? '') }
    sr.onerror = () => { if (!gotResult) onEnd() }
    sr.onend = () => { if (!gotResult) onEnd() }
    sr.start()
    return sr
  }

  const IDLE_MS = 4000

  // ─── RESET UI (tylko wiz.) ──────────────────────────────────────────────
  const resetUI = () => {
    if (idleTimerRef.current) { clearTimeout(idleTimerRef.current); idleTimerRef.current = null }
    stopMagicPhrases()
    setSearchQuery('')
    setSearchAnimated(false)  // ← Reset trybu animacji LLM
    setMicStatus('default')
    setNoteSubmitState('idle')
  }

  // ─── RESET WYNIKÓW WYSZUKIWANIA → przywróć pełną listę konwersacji
  const resetSearchResults = () => {
    searchResultsActiveRef.current = false
    searchAnimatingRef.current = false  // Odblokowanie TAP
    search.forceResetSearch()
    setSearchQuery('')
    setSearchAnimated(false)
    setSearchCompleted(false)
    setMicStatus('default')
    if (idleTimerRef.current) { clearTimeout(idleTimerRef.current); idleTimerRef.current = null }
    pwaApi.getConversations().then(r => conv.setConversationItems(r.items)).catch(() => { })
  }


  // ─── RESET SESJI (pełny) ─────────────────────────────────────────────
  const resetE1 = () => {
    if (searchResultsActiveRef.current) {
      resetSearchResults()
    } else {
      resetUI()
      onResetSession()
    }
  }

  idleResetRef.current = resetE1

  // Gdy E1 staje się nieaktywny — zatrzymaj TYLKO micStatus; frazy magiczne
  // niech odjeżdżają razem z E1 w lewo podczas animacji slide-left
  useEffect(() => {
    if (!isActive) {
      setMicStatus('default')
    }
  }, [isActive])

  // Gdy NOT_BUSINESS, MOBBING_WARNING lub PRIVACY_WARNING: zatrzymaj frazy magiczne, pokaż komunikat, zresetuj UI
  useEffect(() => {
    if (conv.guardStatus === 'NOT_BUSINESS') {
      stopMagicPhrases()
      setPreparingPhraseColor('red')
      setPreparingPhrase('To nie jest notatka służbowa — anulowano.')
      const t = setTimeout(() => {
        setPreparingPhrase(null)
        conv.resetConversation()
        setMicStatus('default')
      }, 2500)
      return () => clearTimeout(t)
    }
    if (conv.guardStatus === 'MOBBING_WARNING') {
      stopMagicPhrases()
      setPreparingPhraseColor('red')
      setPreparingPhrase('Wykryto treści mobbingowe — sformułuj notatkę inaczej.')
      const t = setTimeout(() => {
        setPreparingPhrase(null)
        conv.resetConversation()
        setMicStatus('default')
      }, 3000)
      return () => clearTimeout(t)
    }
    if (conv.guardStatus === 'PRIVACY_WARNING') {
      stopMagicPhrases()
      setPreparingPhraseColor('red')
      setPreparingPhrase('Wykryto dane wrażliwe — usuń informacje medyczne/prywatne.')
      const t = setTimeout(() => {
        setPreparingPhrase(null)
        conv.resetConversation()
        setMicStatus('default')
      }, 3000)
      return () => clearTimeout(t)
    }
  }, [conv.guardStatus])

  // Gdy zewnętrzny reset (np. powrót E3→E1) — zatrzymaj frazy magiczne, wyczyść wyszukiwanie
  useEffect(() => {
    if (resetSignal === 0) return
    stopMagicPhrases()
    setMicStatus('default')
    search.forceResetSearch()
    searchAnimatingRef.current = false
    searchResultsActiveRef.current = false
    setSearchQuery('')
    setSearchAnimated(false)
    setSearchCompleted(false)
  }, [resetSignal])

  // ← POPRAWKA: Reset searchAnimated gdy searchQuery zostaje wyczyszczony
  useEffect(() => {
    if (!searchQuery && searchAnimated) {
      setSearchAnimated(false)
      setSearchCompleted(false)
    }
  }, [searchQuery, searchAnimated])

  const clearIdleTimer = () => {
    if (idleTimerRef.current) { clearTimeout(idleTimerRef.current); idleTimerRef.current = null }
  }

  // Resetuj idle timer przy każdej interakcji z listą
  const resetIdleTimer = () => {
    if (micStatus === 'working' || micStatus === 'pushed') return
    clearIdleTimer()
    if (!conv.selectedEmployee && !searchQuery) return
    idleTimerRef.current = setTimeout(() => {
      idleTimerRef.current = null
      idleResetRef.current()
    }, IDLE_MS)
  }

  // Idle timer startuje gdy coś jest wybrane/wyszukane i mic jest idle
  // NIE startuje gdy trwa pipeline wyszukiwania RAG (searchAnimatingRef.current)
  useEffect(() => {
    if (!isActive) { clearIdleTimer(); return }   // nie odpala gdy E1 niewidoczny
    if (micStatus === 'working' || micStatus === 'pushed') { clearIdleTimer(); return }
    if (searchAnimatingRef.current) { clearIdleTimer(); return }  // pipeline aktywny — brak idle timera
    if (!conv.selectedEmployee && !searchQuery) { clearIdleTimer(); return }
    clearIdleTimer()
    idleTimerRef.current = setTimeout(() => {
      idleTimerRef.current = null
      idleResetRef.current()
    }, IDLE_MS)
    return () => { if (idleTimerRef.current) clearTimeout(idleTimerRef.current) }
  }, [conv.selectedEmployee, searchQuery, micStatus, isActive, search.state.phase])

  const handleVoiceSearch = (onDone: () => void) => {
    // Wyniki widoczne — tap zamyka i wraca do idle
    if (search.state.phase === 'results') {
      search.dismissSearch()
      searchAnimatingRef.current = false
      searchResultsActiveRef.current = false
      onDone()
      return
    }
    if (search.state.phase === 'searching') return
    startVoiceSearch(onDone)
  }

  const startVoiceSearch = (onDone: () => void) => {
    setSearchQuery('')
    setMicStatus('pushed')
    if (voiceSearchTimer.current) clearTimeout(voiceSearchTimer.current)
    if (speechRef.current) { try { speechRef.current.stop() } catch (_) { } }
    speechRef.current = startSpeech(
      (text) => { handleVoiceSearchResult(text, onDone) },
      () => { onDone(); setMicStatus('default') }
    )
    voiceSearchTimer.current = setTimeout(() => {
      if (speechRef.current) { try { speechRef.current.stop() } catch (_) { } }
      onDone(); setMicStatus('default')
    }, 5000)
  }

  const handleVoiceSearchResult = (text: string, onDone?: () => void) => {
    if (voiceSearchTimer.current) clearTimeout(voiceSearchTimer.current)
    const q = text.trim()
    if (!q) { if (onDone) onDone(); setMicStatus('default'); return }

    setMicStatus('default')
    if (onDone) onDone()
    searchCooldownRef.current = true
    setTimeout(() => { searchCooldownRef.current = false }, 1000)

    // ZAWSZE używamy wyszukiwania kontekstowego RAG (useSearchV2 → SearchAnimationPipeline)
    setSearchAnimated(true)
    setSearchCompleted(false)
    searchAnimatingRef.current = true  // Blokada TAP do kroku 9
    setSearchQuery('')
    setTimeout(() => setSearchQuery(q), 0)

    console.log('[RAG] Starting contextual search via useSearchV2 for:', q)
    search.startSearch(q)
  }

  const handleDeleteEmployee = (id: number) => {
    pwaApi.deleteEmployee(id).catch(() => { })
    conv.setApiEmployees(prev => prev ? prev.filter(e => e.id !== id) : prev)
  }

  const handleDeleteConversation = (id: number) => {
    pwaApi.deleteConversationItem(id).catch(() => { })
    conv.setConversationItems(prev => prev ? prev.filter(c => c.conversation_id !== id) : prev)
  }

  // Wywoływane gdy EmployeeList zakończy pipeline 9-krokowy (krok 9) - odblokowanie TAP
  const handleSearchAnimationEnd = () => {
    searchAnimatingRef.current = false
    console.log('[TAP] Unblocked - search animation completed')
  }

  // Failsafe: odblokuj TAP gdy wyszukiwanie wraca do idle
  useEffect(() => {
    if (search.state.phase === 'idle') {
      searchAnimatingRef.current = false
    }
  }, [search.state.phase])

  const doNavigate = (fullText: string) => {
    console.log('[Screen1] doNavigate called with:', fullText.slice(0, 50))
    pendingNavigateRef.current = null
    // Kolejność priorytetów: heurystyka > LLM detected > user selected > fallback
    const name = heuristicNameRef.current || detectedNameRef.current || selectedEmployeeRef.current || 'PracownikX'
    const ap = analyzePromiseRef.current
    console.log('[Screen1] doNavigate - name:', name, 'has ap:', !!ap)
    heuristicNameRef.current = null
    detectedNameRef.current = null
    llmRunningRef.current = false
    selectedEmployeeRef.current = ''  // czyść po nawigacji
    analyzePromiseRef.current = null
    setSearchQuery('')
    console.log('[Screen1] doNavigate - calling onNavigateTo2')
    onNavigateTo2(fullText, name, ap ?? undefined, hardwarePttNavRef.current)
  }

  const triggerNavigate = (fullText: string) => {
    console.log('[Screen1] triggerNavigate called, llmRunning:', llmRunningRef.current)
    // Jeśli analyzeNote jeszcze trwa (scenariusz C: brak heurystyki) — zaczekaj max 3s
    // Scenariusz A/B: heurystyka lub user selected — nawigacja natychmiast (llmRunningRef = false)
    if (llmRunningRef.current) {
      console.log('[Screen1] triggerNavigate - waiting for LLM (3s timeout)')
      pendingNavigateRef.current = fullText
      const deadline = setTimeout(() => {
        if (pendingNavigateRef.current) doNavigate(pendingNavigateRef.current)
      }, 3000)  // max 3s na LLM (zamiast 8s)
        // Wyczyść deadline gdy LLM skończy wcześniej (obsługiwane w onSpeechFinal)
        ; (window as any).__mlotLlmDeadline = deadline
      return
    }
    console.log('[Screen1] triggerNavigate - navigating immediately')
    doNavigate(fullText)
  }

  onDoneRef.current = (fullText: string, meta?: DictationDoneMeta) => {
    checkpoint('SCREEN1', 'onDone received', { fullText: fullText.slice(0, 100), meta, wasHardwarePtt: pttAiModeRef.current })
    console.log('[Screen1] onDoneRef called with:', fullText.slice(0, 50))

    // Auto-Submit hands-free: przechwyć TERAZ, zanim `pttAiModeRef` zostanie
    // wyzerowany w następnej linii — `doNavigate` (wywołane później, czasem
    // asynchronicznie po LLM) musi wiedzieć, że to nagranie zaczął fizyczny PTT.
    hardwarePttNavRef.current = pttAiModeRef.current
    const wasHardwarePtt = pttAiModeRef.current
    pttAiModeRef.current = false

    // ── Detektor słów kluczowych — WSPÓLNY dla dotyku i sprzętowego PTT ────────
    // Dokładnie ten sam `isIncident()`, którego używa pole „Mów lub napisz…"
    // (`handleTypedNoteSubmit`) oraz `goE1toE2` — jedna bramka niezależnie od
    // źródła transkryptu (dotknięcie DuzyMic, gruszka USB, słuchawka BT,
    // symulacja F1/Spacja).
    //
    //   • treść NIE będąca zgłoszeniem → cicha notatka offline (kolejka Dexie +
    //     triggerSync) i optymistyczny kafelek na liście — tekst zostaje NA
    //     EKRANIE, BEZ nawigacji do formularza E2. (Sprzętowy PTT nigdy nie
    //     trafia do asystenta AI — decyzja Input-Only P0 — a dotknięcie to
    //     zwykła szybka notatka, nie odprawa.)
    //   • zgłoszenie („awaria", „wyciek", „brak na magazynie"…) → cichy zapis
    //     PLUS (po ciszy) nawigacja w tor incydentu przez `triggerNavigate`
    //     (`hardwarePttNavRef` niesie auto-submit dla E2b/E3b przy PTT).
    if (!isIncident(fullText)) {
      dbg((wasHardwarePtt ? 'PTT' : 'TOUCH') + '_NOTE — nie-incydent, cichy zapis notatki: ' + fullText.slice(0, 40))
      stopMagicPhrases()
      setMicStatus('default')
      // Transkrypt czyścimy ręcznie — tej ścieżce (w przeciwieństwie do pola
      // tekstowego) nikt inny tego nie zrobi.
      const saved = saveSilentNote(fullText)
      conv.setTranscriptionText('')
      // `saveSilentNote` zwraca false, gdy nie było czego zapisać (< MIN_NOTE_LEN)
      // — offline oznacza to brak lokalnej transkrypcji. Zamiast po cichu porzucić
      // (poprzednio: no-op, zero informacji), powiedz użytkownikowi, co się stało:
      //   • nagranie w kolejce (Android) → notatka pojawi się po sieci,
      //   • brak nagrania (desktop/iOS)  → trzeba wpisać ręcznie.
      if (!saved) {
        if (meta?.offlineAudioQueued) {
          // Kafelek notatki OD RAZU (parytet z online) — treść wskoczy po sieci.
          if (meta.audioHash) addVoicePlaceholder(meta.audioHash)
          setNoteSubmitState('voice-queued')
        } else if (!isOnline) {
          setNoteSubmitState('voice-offline')
        }
      }
      return
    }
    dbg((wasHardwarePtt ? 'PTT' : 'TOUCH') + '_INCIDENT — trasuję jak zgłoszenie')

    // ── Zgłoszenie: cichy zapis do kolejki Dexie + (po ciszy) nawigacja E2 ────
    // Stary tor odprawy (E2/E3 briefing) zostaje nietknięty — przejściowy stan
    // wzorca Strangler.
    saveSilentNote(fullText)

    pendingDoneRef.current = fullText
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current)
    }
    // R2: adaptive timer — jeśli imię znane (heurystyka, LLM, lub user wybrał)
    // skracamy oczekiwanie do 500ms (user widział tekst w czasie rzeczywistym)
    // w przeciwnym razie — standardowe 2000ms (czeka na LLM max 3s w triggerNavigate)
    const hasName = heuristicNameRef.current || detectedNameRef.current || selectedEmployeeRef.current
    const delay = (!llmRunningRef.current && hasName) ? 500 : 2000
    dbg('DONE_DELAY:' + delay)
    console.log('[Screen1] onDoneRef - starting silence timer, delay:', delay, 'hasName:', hasName)
    silenceTimerRef.current = setTimeout(() => {
      silenceTimerRef.current = null
      const text = (pendingDoneRef.current ?? fullText).trim()
      pendingDoneRef.current = null
      console.log('[Screen1] silence timer fired, text length:', text.length)
      if (text.length < 3) { dbg('SKIP — za krótki tekst: ' + text.length); setMicStatus('default'); return }
      // Frazy magiczne startują po 250ms od końca timera
      console.log('[Screen1] starting magic phrases and navigation')
      if (magicStartTimerRef.current) clearTimeout(magicStartTimerRef.current)
      magicStartTimerRef.current = setTimeout(() => {
        magicStartTimerRef.current = null
        startMagicPhrases()
      }, 250)
      triggerNavigate(text)
    }, delay)
  }

  const dictation = useDictation({
    onSegment: (_text, full) => {
      dbg('SEG[' + full.length + ']:' + full.slice(0, 40))
      // Heurystyka: szukaj imienia w tekście w czasie rzeczywistym (bez czekania na LLM)
      if (!heuristicNameRef.current && !selectedEmployeeRef.current) {
        const hName = extractNameHeuristic(full)
        if (hName) {
          heuristicNameRef.current = hName
          dbg('HEUR_NAME:' + hName)
        }
      }
      const name = detectedNameRef.current || heuristicNameRef.current || conv.selectedEmployee
      const prefix = name ? `${name}: ` : ''
      conv.setTranscriptionText(prefix + full)
      // Resetuj silence timer — tekst nadal płynie
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current)
        silenceTimerRef.current = null
      }
    },
    onSpeechFinal: (_text, full) => {
      dbg('FINAL:' + full.slice(0, 30))
      // Sprzętowy PTT: o trasie (incydent/notatka) decyduje `onDoneRef` dopiero
      // po zakończeniu nagrania (isIncident) — tu pomijamy detekcję imienia /
      // analyzeNote starej notatki, bo mogłaby to być cicha notatka bez formularza.
      if (pttAiModeRef.current) return
      if (!isOnline) return
      dbg(`FINAL_CHK sel=${!!selectedEmployeeRef.current} heur=${!!heuristicNameRef.current} det=${!!detectedNameRef.current}`)

      // Scenariusz A: user wybrał pracownika z listy — nie potrzebujemy LLM
      if (selectedEmployeeRef.current) {
        dbg('FINAL_SKIP — user selected')
        return
      }

      // Scenariusz B: heurystyka znalazła imię — użyj natychmiast, waliduj w tle
      if (heuristicNameRef.current) {
        dbg('FINAL_HEUR:' + heuristicNameRef.current)
        selectedEmployeeRef.current = heuristicNameRef.current
        conv.setSelectedEmployee(heuristicNameRef.current)
        conv.setTranscriptionText(`${heuristicNameRef.current}: ${full}`)

        // Walidacja w tle: sprawdź czy LLM zgadza się z heurystyką
        const ap = pwaApi.analyzeNote(full)
        analyzePromiseRef.current = ap
        ap.then(result => {
          if (result.detectedName && result.detectedName !== 'PracownikX' && result.detectedName !== heuristicNameRef.current) {
            // LLM zwrócił inne imię niż heurystyka — popraw w tle (user nie widzi opóźnienia)
            dbg('HEUR_CORRECT:' + result.detectedName + ' (was ' + heuristicNameRef.current + ')')
            detectedNameRef.current = result.detectedName
            // TODO: updateEmployee w tle (implementacja w useConversation)
          }
        }).catch(() => { })
        return
      }

      // Scenariusz C: brak heurystyki — czekamy na LLM (max 3s)
      if (detectedNameRef.current || llmRunningRef.current) {
        dbg('FINAL_SKIP — already running')
        return
      }

      llmRunningRef.current = true
      dbg('FINAL_CALLING_LLM')
      const ap = pwaApi.analyzeNote(full)
      analyzePromiseRef.current = ap
      ap.then(result => {
        llmRunningRef.current = false
        if (result.detectedName && result.detectedName !== 'PracownikX') {
          detectedNameRef.current = result.detectedName
          selectedEmployeeRef.current = result.detectedName
          conv.setSelectedEmployee(result.detectedName)
          conv.setTranscriptionText(`${result.detectedName}: ${result.cleanText || full}`)
        }
        // Jeśli nawigacja czekała na ten wynik — odpal teraz
        if (pendingNavigateRef.current) {
          if ((window as any).__mlotLlmDeadline) { clearTimeout((window as any).__mlotLlmDeadline); (window as any).__mlotLlmDeadline = null }
          doNavigate(pendingNavigateRef.current)
        }
      })
        .catch(() => {
          llmRunningRef.current = false
          if (pendingNavigateRef.current) {
            if ((window as any).__mlotLlmDeadline) { clearTimeout((window as any).__mlotLlmDeadline); (window as any).__mlotLlmDeadline = null }
            doNavigate(pendingNavigateRef.current)
          }
        })
    },
    onDone: (full, meta) => { dbg('DONE:' + full.slice(0, 30)); onDoneRef.current(full, meta) },
    onError: (msg) => {
      dbg('ERR:' + msg.slice(0, 40))
      if (dictTimer60.current) clearTimeout(dictTimer60.current)
      setMicStatus('default')
      if (msg?.includes('403')) onUnauthorized('Subskrypcja wygasła lub konto zostało dezaktywowane.')
    },
  })

  // Czeka (max 10s, sprawdzając co 300ms), aż synchronizacja się skończy, PRZED
  // rozpoczęciem dyktowania — nie przerywa niczego, co już trwa, tylko odkłada
  // start o krótką chwilę w rzadkim przypadku, gdy akurat trwa sync. W typowym
  // przypadku (sync nieaktywny) zwraca się natychmiast, bez żadnej zwłoki.
  const waitForSyncToFinish = async (): Promise<void> => {
    const SYNC_WAIT_MAX_MS = 10000
    const SYNC_WAIT_STEP_MS = 300
    let waited = 0
    while (isSyncingRef.current && waited < SYNC_WAIT_MAX_MS) {
      await new Promise(r => setTimeout(r, SYNC_WAIT_STEP_MS))
      waited += SYNC_WAIT_STEP_MS
    }
  }

  const handleDictationStart = async () => {
    checkpoint('SCREEN1', 'handleDictationStart')
    if (searchCooldownRef.current) { dbg('COOLDOWN — ignoruj start'); return }
    await waitForSyncToFinish()
    dbg('START')
    checkpoint('SCREEN1', 'dictation.start called')
    detectedNameRef.current = null
    heuristicNameRef.current = null  // czyść heurystykę przy nowej dyktacji
    llmRunningRef.current = false
    selectedEmployeeRef.current = ''  // sync — czyść przed startem nagrywania
    analyzePromiseRef.current = null
    // Wyczyść search, transkrypt i selectedEmployee — nowa dyktacja = nowa sesja imienia
    setSearchQuery('')
    conv.setTranscriptionText('')
    conv.setSelectedEmployee('')
    setNoteSubmitState('idle')  // nowa dyktacja kasuje baner po poprzedniej notatce
    setMicStatus('working')
    // Hybrid STT: `useDictation` sam decyduje o silniku (Deepgram / Web Speech
    // fallback offline) — Screen1 woła zawsze tę samą parę start/stop.
    dictation.start()
    if (dictTimer60.current) clearTimeout(dictTimer60.current)
    dictTimer60.current = setTimeout(() => {
      sigTimeout()
      try { navigator.vibrate?.(200) } catch (_) { }
      handleDictationEnd()
    }, 45000)
  }

  const handleDictationEnd = () => {
    checkpoint('SCREEN1', 'handleDictationEnd')
    dbg('END')
    console.log('[Screen1] handleDictationEnd called')
    if (dictTimer60.current) clearTimeout(dictTimer60.current)
    dictation.stop()
  }

  // ── Push-To-Talk: fizyczny przycisk słuchawki Bluetooth (Media Session API) ──
  // Opakowania przez ref+useCallback: handleDictationStart/End zmieniają tożsamość
  // co render (domykają wiele zmiennych stanu), a hook Media Session montuje się
  // raz — przez ref zawsze woła NAJŚWIEŻSZĄ wersję, bez re-rejestrowania handlerów
  // i bez zbędnych re-renderów. Przycisk słuchawki uruchamia ten sam tor nagrywania
  // co dotknięcie DuzyMic, ale (Input-Only, decyzja P0) wynik zawsze ląduje jako
  // zgłoszenie/notatka — nigdy jako zapytanie do asystenta AI (patrz `onDoneRef`).
  const dictationStartRef = useRef<() => void>(() => { })
  const dictationEndRef = useRef<() => void>(() => { })
  dictationStartRef.current = handleDictationStart
  dictationEndRef.current = handleDictationEnd

  // FIX DESYNCHRONIZACJI: po auto-stopie z ciszy (watchdog VAD) mostek musi
  // wrócić do „idle", żeby kolejne kliknięcie sprzętowego przełącznika znów
  // znaczyło „start". `ptt` powstaje niżej — sięgamy po jego `resetAllSources`
  // przez ref (spójnie z `dictationStartRef`/`dictationEndRef`).
  const pttResetRef = useRef<() => void>(() => { })

  // „Bulletproof" tor natywnego mostka: bezpieczny start z TTS („Mów", z
  // bezpiecznikiem Promise.race 1200 ms) + detekcja ciszy (VAD 10 s). Nie
  // prowadzi własnego MediaRecordera — Deepgram/`useDictation` dalej robi
  // transkrypcję; ten hook dokłada komunikaty głosowe i watchdog ciszy.
  const pttVoice = usePttVoiceCapture({
    captureBlob: false,
    onBeginRecording: () => { void dictationStartRef.current() },
    onEndRecording: () => { dictationEndRef.current() },
    onSilenceReset: () => { pttResetRef.current() },
  })
  const pttVoiceRef = useRef(pttVoice)
  pttVoiceRef.current = pttVoice

  const handleBluetoothStart = useCallback(() => {
    // Zaznacz, że to PTT — onDoneRef przekieruje transkrypt do zgłoszenia/notatki
    // (isIncident), NIGDY do asystenta AI.
    pttAiModeRef.current = true
    // Nowe nagranie = koniec życia poprzedniej odpowiedzi na ekranie (inaczej
    // baner z poprzedniej tury wisiałby pod nowym dyktowaniem).
    clearAiAnswer()
    // TTS „Mów" → (po rozwiązaniu obietnicy) dictation.start() → watchdog ciszy.
    void pttVoiceRef.current.start()
  }, [clearAiAnswer])
  const handleBluetoothStop = useCallback(() => { pttVoiceRef.current.stop() }, [])

  // Dotyk ekranu (DuzyMic) = stara notatka: kasuje tryb PTT przed startem.
  const handleTouchDictationStart = useCallback(() => {
    pttAiModeRef.current = false
    void dictationStartRef.current()
  }, [])

  // ── Menedżer PTT: WebHID (gruszka USB) + Media Session (słuchawka BT) +
  // symulacja klawiaturowa (F1/Spacja, DEV-only) scalone w JEDEN strumień
  // „wciśnięto → dyktuj → puszczono", wpięty w ten sam tor Input-Only co
  // dotąd (`handleBluetoothStart/Stop` → `pttAiModeRef`). Szczegóły scalania
  // trzech źródeł (w tym ochrona przed podwójnym startem, gdy dwa źródła są
  // trzymane naraz) żyją w `usePttManager`.
  const ptt = usePttManager({
    onPress: handleBluetoothStart,
    onRelease: handleBluetoothStop,
  })
  pttResetRef.current = ptt.resetAllSources

  return (
    <div className="relative w-full h-full">

      {preparingPhrase && (
        <div style={{
          position: 'absolute', inset: 0, zIndex: 50,
          background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(6px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          pointerEvents: 'none',
        }}>
          <div key={preparingPhrase} style={{ textAlign: 'center' }}>
            {(() => {
              const parts = preparingPhrase.split(' — ');
              return (
                <>
                  <span style={{
                    color: preparingPhraseColor === 'red' ? 'rgba(255,59,48,0.95)' : 'rgba(0,255,85,0.85)',
                    fontSize: '24px', fontWeight: 300,
                    fontFamily: '"Inter", sans-serif',
                    letterSpacing: '0.5px',
                    display: 'block',
                    marginBottom: '8px',
                    animation: 'magicFadeIn 0.5s ease forwards',
                  }}>{parts[0]}</span>
                  {parts[1] && (
                    <span style={{
                      color: preparingPhraseColor === 'red' ? 'rgba(255,59,48,0.75)' : 'rgba(0,255,85,0.7)',
                      fontSize: '18px', fontWeight: 300,
                      fontFamily: '"Inter", sans-serif',
                      letterSpacing: '0.3px',
                      display: 'block',
                      animation: 'magicFadeIn 0.5s ease forwards',
                    }}>{parts[1]}</span>
                  )}
                </>
              );
            })()}
          </div>
          <style>{`@keyframes magicFadeIn { from { opacity:0; transform:translateY(4px); } to { opacity:1; transform:translateY(0); } }`}</style>
        </div>
      )}

      {/* Parowanie fizycznej gruszki USB (WebHID) — róg ekranu, nie koliduje z
          resztą layoutu sterowaną przez `positions` (E1 jest bardzo ciasno
          rozmieszczony procentowo). Ukrywa się do neutralnej pigułki na
          przeglądarkach bez WebHID (Safari/Firefox) — patrz `WebHidPairButton`. */}
      {/* {ptt.isWebHidSupported && (
        <div style={{ position: 'absolute', top: 14, right: 14, zIndex: 45 }}>
          <WebHidPairButton
            isSupported={ptt.isWebHidSupported}
            isConnected={ptt.isWebHidConnected}
            deviceNames={ptt.webHidDeviceNames}
            onRequestDevice={ptt.requestUsbDevice}
          />
        </div>
      )} */}

      {/* E1: Tytuł na górze */}
      <div style={{
        position: 'absolute', top: `${positions.titleTop}%`, left: 0, right: 0,
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        color: '#D9D9D9', textAlign: 'center',
        fontFamily: '"Inter", sans-serif', fontSize: 'clamp(24px, 4vw, 32px)',
        fontWeight: 200, letterSpacing: '3px',
        fontFeatureSettings: "'liga' off, 'clig' off", whiteSpace: 'nowrap',
      }}>BlackApp</div>

      {/* E1: Lista kafelków - przesuwa się w GÓRĘ zsynchronizowana z SearchBar (600ms + 200ms delay) */}
      <div style={{
        position: 'absolute',
        top: `${micStatus === 'working' ? positions.employeeListTopWorking : positions.employeeListTop}%`,
        left: '4.8%', right: '4.8%',
        zIndex: listModalOpen ? 60 : 20,
        transition: micStatus === 'working'
          ? 'top 600ms cubic-bezier(0.25, 1, 0.2, 1) 200ms'  // Rozwijanie: 600ms + 200ms delay jak SearchBar
          : 'top 600ms cubic-bezier(0.25, 1, 0.2, 1)',        // Zwijanie: 600ms bez delay
      }}>
        <EmployeeList
          onSelectEmployee={(name) => { selectedEmployeeRef.current = name; conv.setSelectedEmployee(name); setMicStatus('default'); setSearchQuery(''); setSearchAnimated(false); setSearchCompleted(false); searchAnimatingRef.current = false }}
          searchQuery={searchQuery}
          searchAnimated={searchAnimated}
          searchCompleted={searchCompleted}
          noteUpdate={conv.noteUpdate}
          apiEmployees={conv.apiEmployees}
          conversationItems={conv.conversationItems}
          onDeleteEmployee={handleDeleteEmployee}
          onDeleteConversation={handleDeleteConversation}
          onInteraction={resetIdleTimer}
          resetSignal={resetSignal}
          onSearchAnimationEnd={handleSearchAnimationEnd}
          searchPipelineState={search.state}
          onModalStateChange={setListModalOpen}
        />
      </div>

      {/* E1: SearchBar w ŚRODKU - rozwija się W GÓRĘ do listy kafelków */}
      <div style={{
        position: 'absolute',
        top: `${positions.searchBarTop}%`,
        left: '4.8%', right: '4.8%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        zIndex: 30,
      }}>
        <SearchBar
          status={micStatus}
          expandDirection="up"  // Rozwija się w GÓRĘ
          expandHeight={micStatus === 'working' ? positions.searchBarBottomExpand : 0}
          value={micStatus === 'working' ? conv.transcriptionText : micStatus === 'pushed' ? searchQuery : undefined}
          selectedEmployee={conv.selectedEmployee}
          onChange={(val) => conv.setTranscriptionText(val)}
          onSearchSubmit={(name) => {
            const q = name.trim()
            if (q) {
              searchAnimatingRef.current = true
              setSearchAnimated(true)
              setSearchCompleted(false)
              search.startSearch(q)
            }
            selectedEmployeeRef.current = ''
            conv.setSelectedEmployee('')
            setMicStatus('default')
          }}
          onSearchChange={(val) => { selectedEmployeeRef.current = ''; conv.setSelectedEmployee(''); setSearchQuery(val) }}
          onVoiceSearchResult={handleVoiceSearchResult}
          onNoteSubmit={handleTypedNoteSubmit}
        />
      </div>

      {/* E1: DuzyMic NA DOLE - STAŁA pozycja (nie zmienia się przy dyktowaniu) */}
      <div style={{
        position: 'absolute',
        top: `${positions.mlotButtonTop}%`,  // Stała pozycja
        left: 0, right: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        zIndex: 40,
      }}>
        <MlotButton
          status={micStatus}
          scale={micScale}
          onVoiceSearch={handleVoiceSearch}
          onDictationStart={handleTouchDictationStart}
          onDictationEnd={handleDictationEnd}
          onWarmup={() => dictation.warmup()}
          onWarmupCancel={() => dictation.cancel()}
          onDebug={dbg}
        />
      </div>

      {/* TokenBar na dole */}
      <div style={{
        position: 'absolute',
        bottom: `${positions.tokenBarBottom}%`,
        left: '4.8%', right: '4.8%',
        zIndex: 10,
      }}>
        <SyncStatusBar
          pendingCount={pendingSyncCount}
          syncing={isSyncing}
          dimmed={!isOnline}
        />
      </div>

      {/* Krok 7 walidacji FAZY 1: panel diagnostyki dyktowania offline. Renderuje
          `null`, dopóki nie włączy go flaga `?sttdiag=1` — zero śladu w UI. */}
      <SttDiagnosticsOverlay />

      {/* „Uczciwy UX”: po zakończeniu dyktowania mówimy PRAWDĘ o losie notatki.
          Kolory spójne z globalnym paskiem: czerwony = offline, niebieski = sync. */}
      {/* {noteSubmitState !== 'idle' && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: 'absolute', left: '50%', bottom: '12%', transform: 'translateX(-50%)',
            maxWidth: '84%', padding: '10px 18px', borderRadius: 12, zIndex: 60,
            background: noteSubmitState === 'processing'
              ? 'rgba(59,130,246,0.94)'    // niebieski = online / sync w toku
              : 'rgba(220,38,38,0.94)',    // czerwony = offline (queued / voice-*)
            color: '#fff', fontSize: 13, fontWeight: 600, letterSpacing: 0.3,
            textAlign: 'center', boxShadow: '0 8px 28px rgba(0,0,0,0.5)',
          }}
        >
          {noteSubmitState === 'voice-offline' ? NOTE_MSG_VOICE_OFFLINE
            : noteSubmitState === 'voice-queued' ? NOTE_MSG_VOICE_QUEUED
              : noteSubmitState === 'queued' ? NOTE_MSG_OFFLINE
                : NOTE_MSG_ONLINE}
        </div>
      )} */}

      {/* Woki-Toki: odpowiedź asystenta AI (PTT) — ta sama treść, którą
          `useAiWalkieTalkie` przeczytał na głos (TTS). Bez tego bannera
          telefon "mówił", ale ekran nie pokazywał żadnego śladu odpowiedzi. */}

      {/* Debug checkpoint panel — widoczny na telefonie jako overlay */}
      <AppDebugPanel />

    </div>
  )
}
